import cn from 'classnames';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefinementList } from 'react-instantsearch';
import throttle from 'lodash.throttle';
import { refinementListClassNames } from '../refinementListClassNames';

import styles from './ScrollableRefinementList.module.scss';

interface RefinementListProps {
  classNames: {
    list: string;
    showMore: string;
    [key: string]: string;
  };
}

interface ScrollableRefinementListProps {
  children: React.ReactElement<typeof RefinementList> & {
    props: RefinementListProps;
  };
  /**
   * Auto-click "Show more" once, the first time this becomes true, so a
   * selected value beyond the collapsed limit (e.g. a low-count brand) isn't
   * hidden behind a collapsed list the customer has to know to expand. Set
   * this from whether the facet currently has an active refinement (e.g.
   * AllFiltersPanel's/FilterPill's own `getRefinementCount(attribute) > 0`).
   * Re-arms only on a false→true transition (all refinements cleared, then a
   * new one selected) — callers pass a boolean (`count > 0`), so this does
   * not re-trigger on every additional selection while already active.
   * Only for this auto-expand: once the list re-renders with the extra
   * items, the first checked item is also scrolled into view (nearest
   * scrollable ancestor only, not the page) so it's visible without an
   * extra manual scroll — a manual "Show more" click never scrolls.
   */
  autoExpand?: boolean;
}

const ScrollableRefinementList: React.FC<ScrollableRefinementListProps> = ({
  children,
  autoExpand = false,
}) => {
  const [isToggled, setIsToggled] = useState(false);

  const observerRef = useRef<MutationObserver | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const collapsedLabelRef = useRef<string | null>(null);
  const hasAutoExpandedRef = useRef(false);
  const pendingScrollToSelectionRef = useRef(false);

  const classNames = children.props.classNames ?? refinementListClassNames;

  const setupObserver = useCallback(() => {
    const showMoreButton = wrapperRef.current?.querySelector<HTMLElement>(
      `[class*="${classNames.showMore}"]`
    );

    const refinementList = wrapperRef.current?.querySelector<HTMLElement>(
      `[class*="${classNames.list}"]`
    );

    if (!refinementList) {
      console.warn('ScrollableFacet: RefinementList not found.');
      return;
    }

    if (!showMoreButton) {
      console.warn('ScrollableFacet: Show More button not found.');
      return;
    }

    if (collapsedLabelRef.current === null) {
      collapsedLabelRef.current = showMoreButton.textContent;
    }

    if (
      autoExpand &&
      !hasAutoExpandedRef.current &&
      showMoreButton.textContent === collapsedLabelRef.current
    ) {
      hasAutoExpandedRef.current = true;
      pendingScrollToSelectionRef.current = true;
      showMoreButton.click();
    }

    const throttledMutationHandler = throttle(() => {
      setIsToggled(showMoreButton.textContent !== collapsedLabelRef.current);

      if (pendingScrollToSelectionRef.current) {
        pendingScrollToSelectionRef.current = false;
        // The `.enabled` class (which makes the list scrollable via
        // `overflow-y: auto`) is applied by the `setIsToggled` call above,
        // but that's a React state update — it hasn't committed to the DOM
        // yet at this point in the same synchronous callback. Calling
        // scrollIntoView now would find an unclipped, non-scrollable list
        // and silently no-op. Wait two frames so the class (and the layout
        // it triggers) has actually painted first.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            refinementList
              .querySelector<HTMLInputElement>('input:checked')
              ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          });
        });
      }
    }, 300);

    observerRef.current = new MutationObserver(throttledMutationHandler);
    observerRef.current.observe(refinementList, { childList: true });

    return () => {
      observerRef.current?.disconnect();
      // Disconnecting the observer stops new mutation records, but a trailing
      // invocation lodash.throttle already scheduled from a mutation just
      // before unmount is not cancelled by that alone — it would still fire
      // later and call setIsToggled after this component is gone.
      throttledMutationHandler.cancel();
    };
  }, [classNames.list, classNames.showMore, autoExpand]);

  useEffect(() => {
    const cleanup = setupObserver();
    return cleanup;
  }, [setupObserver]);

  useEffect(() => {
    if (!autoExpand) {
      hasAutoExpandedRef.current = false;
    }
  }, [autoExpand]);

  return (
    <div
      className={cn(styles.scrollableList, { [styles.enabled]: isToggled })}
      ref={wrapperRef}
    >
      {children}
    </div>
  );
};

export default ScrollableRefinementList;
